<template>
  <main class="public-units">
    <header class="hero">
      <div class="hero-content">
        <img src="/img/osfatun-cuotas-logo.png" alt="OSFATUN - Nuestro plan es cuidarte" />
        <h1>DATOS Y UNIDADES</h1>
        <h3>ENCONTRÁ TU DELEGACIÓN OSFATUN</h3>
      </div>
    </header>

    <section class="units-card">
      <div class="intro">
        <div>
          <p class="kicker">RED DE ATENCIÓN</p>
          <h2>Seleccioná tu provincia y localidad</h2>
          <p>Consultá direcciones, teléfonos, canales de contacto y horarios actualizados.</p>
        </div>
        <span class="count" v-if="filteredDelegations.length">
          {{ filteredDelegations.length }} {{ filteredDelegations.length === 1 ? "unidad" : "unidades" }}
        </span>
      </div>

      <div class="pdf-download">
        <button type="button" :disabled="pdfLoading" @click="downloadPdf">
          {{ pdfLoading ? "GENERANDO PDF…" : "DESCARGAR PDF COMPLETO" }}
        </button>
        <p v-if="pdfError" class="pdf-error">{{ pdfError }}</p>
      </div>

      <div class="filters">
        <label>
          Provincia
          <select v-model="province">
            <option value="">Todas las provincias</option>
            <option v-for="item in provinces" :key="item" :value="item">{{ item }}</option>
          </select>
        </label>
        <label>
          Localidad
          <select v-model="city" :disabled="!province">
            <option value="">Todas las localidades</option>
            <option v-for="item in cities" :key="item" :value="item">{{ item }}</option>
          </select>
        </label>
        <label class="search-field">
          Buscar
          <input v-model.trim="search" type="search" placeholder="Nombre de la delegación" />
        </label>
      </div>

      <p v-if="loading" class="status">Cargando unidades…</p>
      <p v-else-if="error" class="status error">{{ error }}</p>
      <p v-else-if="!filteredDelegations.length" class="status">No encontramos unidades para esos filtros.</p>

      <div v-else class="units-grid">
        <article v-for="delegation in filteredDelegations" :key="delegation.id" class="unit-card">
          <div class="unit-heading">
            <div>
              <span class="type">{{ typeLabel(delegation.type) }}</span>
              <h2><router-link class="unit-link" :to="`/datos-y-unidades/${delegation.id}`">{{ delegation.name }}<span aria-hidden="true">→</span></router-link></h2>
              <p>{{ delegation.city }}, {{ delegation.province }}</p>
            </div>
          </div>

          <dl class="contact-list">
            <div v-if="delegation.address"><dt>Dirección</dt><dd>{{ delegation.address }}<span v-if="delegation.postal_code"> · {{ delegation.postal_code }}</span></dd></div>
            <div v-if="delegation.phone"><dt>Teléfono</dt><dd><a :href="`tel:${cleanPhone(delegation.phone)}`">{{ delegation.phone }}</a></dd></div>
            <div v-if="delegation.whatsapp"><dt>WhatsApp</dt><dd><a :href="`https://wa.me/${cleanPhone(delegation.whatsapp)}`" target="_blank" rel="noopener">{{ delegation.whatsapp }}</a></dd></div>
            <div v-if="delegation.email"><dt>Email</dt><dd><a :href="`mailto:${delegation.email}`">{{ delegation.email }}</a></dd></div>
          </dl>

          <details v-if="scheduleGroups(delegation).length" class="schedules">
            <summary>Ver horarios</summary>
            <div v-for="group in scheduleGroups(delegation)" :key="group.key" class="schedule-group">
              <h3>{{ group.label }}</h3>
              <p v-for="line in group.lines" :key="line">{{ line }}</p>
            </div>
          </details>
        </article>
      </div>
    </section>
  </main>
</template>

<script>
import axios from "axios";
import { downloadDatosUnidadesPdf } from "../../utils/datosUnidadesPdf";

const days = [
  ["monday", "Lunes"], ["tuesday", "Martes"], ["wednesday", "Miércoles"],
  ["thursday", "Jueves"], ["friday", "Viernes"], ["saturday", "Sábado"], ["sunday", "Domingo"],
];

const groups = [
  ["horary", "Atención presencial"],
  ["tele_assistance", "Teleasistencia"],
  ["shift", "Atención con turno previo"],
];

export default {
  name: "PublicDatosUnidades",
  data() {
    return { delegations: [], province: "", city: "", search: "", loading: true, error: "", pdfLoading: false, pdfError: "" };
  },
  computed: {
    apiBase() { return process.env.VUE_APP_ROOT_API || "https://api.osfatun.com.ar"; },
    provinces() {
      return [...new Set(this.delegations.map(item => item.province).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es"));
    },
    cities() {
      return [...new Set(this.delegations.filter(item => item.province === this.province).map(item => item.city).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es"));
    },
    filteredDelegations() {
      const needle = this.search.toLocaleLowerCase("es");
      return this.delegations.filter(item => {
        if (this.province && item.province !== this.province) return false;
        if (this.city && item.city !== this.city) return false;
        if (!needle) return true;
        return String(item.name || "").toLocaleLowerCase("es").includes(needle);
      }).sort((a, b) => this.typeOrder(a.type) - this.typeOrder(b.type) || (a.name || "").localeCompare(b.name || "", "es"));
    },
  },
  watch: {
    province() { this.city = ""; },
  },
  mounted() {
    axios.get(`${this.apiBase}/api/delegations`)
      .then(({ data }) => { this.delegations = Array.isArray(data.delegations) ? data.delegations : []; })
      .catch(() => { this.error = "No se pudieron cargar las unidades. Intente nuevamente."; })
      .finally(() => { this.loading = false; });
  },
  methods: {
    async downloadPdf() {
      this.pdfLoading = true;
      this.pdfError = "";
      try {
        const [delegationsResponse, casaCentralResponse] = await Promise.all([
          axios.get(`${this.apiBase}/api/delegations_users`),
          axios.get(`${this.apiBase}/api/getCasaCentral`),
        ]);
        const delegations = Array.isArray(delegationsResponse.data.delegations) ? delegationsResponse.data.delegations : [];
        downloadDatosUnidadesPdf(delegations, casaCentralResponse.data || {});
      } catch (error) {
        this.pdfError = "No se pudo generar el PDF. Intente nuevamente.";
      } finally {
        this.pdfLoading = false;
      }
    },
    typeLabel(type) {
      if (!type) return "Unidad OSFATUN";
      return type.replace(/\b\w/g, letter => letter.toUpperCase());
    },
    typeOrder(type) {
      const normalized = String(type || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
      return { "delegacion propia": 0, "centro medico": 1, "boca de expendio": 2, "delegacion tercerizada": 3 }[normalized] ?? 4;
    },
    cleanPhone(phone) { return String(phone || "").replace(/\D/g, ""); },
    formatTime(time) { return String(time || "").slice(0, 5); },
    scheduleGroups(delegation) {
      return groups.map(([key, label]) => {
        const lines = days.map(([dayKey, dayLabel]) => {
          const morningStart = delegation[`${dayKey}_${key}_morning_start`];
          const morningEnd = delegation[`${dayKey}_${key}_morning_end`];
          const afternoonStart = delegation[`${dayKey}_${key}_afternoon_start`];
          const afternoonEnd = delegation[`${dayKey}_${key}_afternoon_end`];
          const ranges = [];
          if (morningStart && morningEnd) ranges.push(`${this.formatTime(morningStart)} a ${this.formatTime(morningEnd)}`);
          if (afternoonStart && afternoonEnd) ranges.push(`${this.formatTime(afternoonStart)} a ${this.formatTime(afternoonEnd)}`);
          return ranges.length ? `${dayLabel}: ${ranges.join(" y ")}` : null;
        }).filter(Boolean);
        return { key, label, lines };
      }).filter(group => group.lines.length);
    },
  },
};
</script>

<style scoped>
.public-units { min-height: 100vh; padding-bottom: 64px; background: #f4f7f8; color: #264653; }
.hero { min-height: 330px; padding: 42px max(6vw, 24px) 104px; color: #fff; background-color: #007f91; background-image: linear-gradient(rgba(0, 112, 132, .12), rgba(0, 112, 132, .12)), url('/img/osfatun-cuotas-header.png'); background-position: center; background-size: cover; }
.hero-content { width: min(1100px, 100%); margin: 0 auto; display: flex; flex-direction: column; align-items: center; text-align: center; }
.hero img { width: 100%; max-width: 220px; height: auto; margin-bottom: 75px; }
.hero h1 { margin: 0; color: #fff; font-size: 42px; font-weight: 800; line-height: 1.05; }
.hero h3 { margin: 14px 0 0; color: #fff; font-size: 18px; font-weight: 700; line-height: 1.35; }
.units-card { width: min(1220px, 92vw); margin: -52px auto 0; padding: 32px; border-radius: 16px; background: #fff; box-shadow: 0 16px 45px rgba(18, 61, 72, .15); }
.intro { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; }
.intro h2 { margin: 2px 0 6px; color: #00758f; font-size: 25px; }
.intro p { margin: 0; }.intro .kicker { color: #f4a51c; font-size: 12px; font-weight: 800; letter-spacing: .12em; }
.count { flex: 0 0 auto; padding: 8px 12px; border-radius: 999px; background: #edf6f7; color: #00758f; font-size: 13px; font-weight: 700; }
.pdf-download { display: flex; flex-direction: column; align-items: center; margin: 26px 0 8px; }.pdf-download button { min-height: 46px; padding: 0 24px; border: 0; border-radius: 7px; background: #00758f; color: #fff; font: inherit; font-weight: 800; letter-spacing: .03em; cursor: pointer; }.pdf-download button:hover, .pdf-download button:focus { background: #006579; }.pdf-download button:disabled { cursor: wait; opacity: .65; }.pdf-error { margin: 8px 0 0; color: #b42318; font-size: 13px; }
.filters { display: grid; grid-template-columns: 1fr 1fr 1.4fr; gap: 18px; margin: 28px 0 34px; }
.filters label { display: grid; gap: 7px; color: #00758f; font-weight: 700; }
.filters select, .filters input { width: 100%; min-width: 0; height: 44px; padding: 0 12px; border: 1px solid #bed0d4; border-radius: 7px; background: #fff; color: #264653; font: inherit; }
.filters select:disabled { background: #f2f5f6; color: #809399; }
.units-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
.unit-card { padding: 24px; border: 1px solid #dce7e9; border-radius: 12px; background: #fff; box-shadow: 0 8px 24px rgba(27, 72, 82, .07); }
.unit-heading { display: flex; justify-content: space-between; gap: 16px; padding-bottom: 16px; border-bottom: 1px solid #e3ebed; }
.unit-heading h2 { margin: 5px 0 4px; color: #00758f; font-size: 20px; font-weight: 800; line-height: 1.2; }.unit-link { color: inherit; font-weight: 800; }.unit-link span { display: inline-block; margin-left: 7px; opacity: 0; transform: translateX(-7px); transition: opacity .18s ease, transform .18s ease; }.unit-link:hover span, .unit-link:focus span { opacity: 1; transform: translateX(0); }
.unit-heading p { margin: 0; color: #60777e; font-size: 14px; }.type { color: #f4a51c; font-size: 11px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
.contact-list { margin: 17px 0 0; }.contact-list div { display: grid; grid-template-columns: 84px 1fr; gap: 10px; margin: 9px 0; }.contact-list dt { color: #60777e; font-size: 12px; font-weight: 700; text-transform: uppercase; }.contact-list dd { margin: 0; overflow-wrap: anywhere; }.contact-list a { color: #00758f; }
.schedules { margin-top: 18px; border-top: 1px solid #e3ebed; }.schedules summary { padding-top: 16px; color: #00758f; font-weight: 800; cursor: pointer; }.schedule-group { margin-top: 16px; }.schedule-group h3 { margin: 0 0 6px; color: #f4a51c; font-size: 13px; text-transform: uppercase; }.schedule-group p { margin: 3px 0; font-size: 13px; }
.status { padding: 44px 20px; text-align: center; }.status.error { color: #b42318; }
@media (max-width: 800px) { .hero { min-height: 280px; padding: 32px 20px 82px; }.units-card { padding: 22px; }.intro { flex-direction: column; }.filters, .units-grid { grid-template-columns: 1fr; }.search-field { grid-column: auto; } }
@media (max-width: 480px) { .hero h1 { font-size: 34px; }.contact-list div { grid-template-columns: 1fr; gap: 2px; } }
</style>
